import { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Typography, Alert, CircularProgress,
} from "@mui/material";
import { ACCENT } from "./accent";
import { reportData } from "./api/publicSubmissions";

const REASONS = [
  { value: "wrong_specs", label: "Wrong / implausible specs" },
  { value: "duplicate", label: "Duplicate entry" },
  { value: "spam", label: "Spam or junk data" },
  { value: "other", label: "Something else" },
];

/** Public "report bad data" form — keeps the shared catalog trustworthy. */
const ReportDataDialog = ({ open, onClose, defaultSubject = "" }) => {
  const [reason, setReason] = useState("wrong_specs");
  const [subject, setSubject] = useState(defaultSubject);
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const close = () => {
    if (sending) return;
    setDone(false);
    setError(null);
    setDetails("");
    onClose();
  };

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      await reportData({ reason, subject: subject.trim(), details: details.trim() });
      setDone(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>Report a data problem</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Thanks — the report is with the moderators. Community-checked data is what keeps the
            catalog trustworthy.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Spotted a spec that can't be right? Tell us and we'll review it.
            </Typography>
            <TextField select fullWidth size="small" label="What's wrong?" value={reason}
              onChange={(e) => setReason(e.target.value)} sx={{ mb: 1.5 }}>
              {REASONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </TextField>
            <TextField fullWidth size="small" label="Which part / entry? (brand + model)" value={subject}
              onChange={(e) => setSubject(e.target.value)} sx={{ mb: 1.5 }} />
            <TextField fullWidth size="small" multiline minRows={3}
              label="Details (what's wrong, correct value, source…)" value={details}
              onChange={(e) => setDetails(e.target.value)} />
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={sending} sx={{ textTransform: "none" }}>
          {done ? "Close" : "Cancel"}
        </Button>
        {!done && (
          <Button onClick={send} variant="contained" disabled={sending || !subject.trim()}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: ACCENT, textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: ACCENT, filter: "brightness(0.95)" } }}>
            Send report
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportDataDialog;
